import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
// react-router-dom 不再需要 — 纯嵌入式组件
import { Card, Table, Button, Space, Select, Tag, Typography, Row, Col, Statistic, Alert, Tooltip, Badge, Spin, Collapse, message, Divider, Input, DatePicker, Popconfirm, Modal, Popover, Form, InputNumber } from "antd";
import { PlusOutlined, CheckOutlined, SendOutlined, ExclamationCircleOutlined, CloseCircleOutlined, EditOutlined, FilterOutlined, SaveOutlined, FileTextOutlined, InfoCircleOutlined, DollarOutlined, DatabaseOutlined, RobotOutlined, CheckCircleOutlined, DeleteOutlined, UndoOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useInvoiceReview } from "../../context/InvoiceReviewContext";
import workbenchApi from "../../services/workbenchApi";
import EditableCell, { EditableRow } from "./EditableCell";
import AgentAnalysisViewer from "./AgentAnalysisViewer";
import ContractMatchPanel from "./ContractMatchPanel";
import FlightValidationCell from "./FlightValidationCell";
import { getPresignedViewUrl } from "../../../../../api/attachmentApi";
import { ALLOCATION_TYPE_OPTIONS, ALLOCATION_TYPE_LABELS, ALLOCATION_TYPE_COLORS, ALLOCATION_RULE_LABELS, CURRENCY_SYMBOLS, WRITABLE_CURRENCY_CODES, RECONCILIATION_VIEW_FILTER, RECONCILIATION_VIEW_FILTER_LABELS, STANDARD_COST_TYPE_LABELS, STANDARD_COST_TYPE_OPTIONS, getStandardCostTypeLabel, idsIsTerminal } from "../../../constants";
import PermissionGuard from "../../../../../components/auth/PermissionGuard";
import SupplierSelector from "../../../../../components/common/SupplierSelector";
import FlightTimeDisplay from "../../../../../components/DateTimeDisplay/FlightTimeDisplay";
import { useAirports } from "../../../../../hooks/useAirports";
import {
    dirtyFieldList,
    isLineItemModified,
    isLineItemValidationStale,
    lineItemFieldPath,
    resolveLineItemCostType,
    resolveLineItemProductCode,
} from "../../utils/reviewPayloadSemantics";
import { roundMoney, sumMoney } from "../../utils/money";
import {
    formatSignedAmount,
    getInvoiceBalancePresentation,
    getSectionBalancePresentation,
    INVOICE_BALANCE_STATUS,
} from "../../utils/invoiceBalanceDisplay";
import { getLineItemLifecycleStatus, getVoidReason, isLineItemActive, isLineItemInactive } from "../../../utils/revisionDisplay";

dayjs.extend(utc);

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

// 货币符号映射现已提取至 settlement constants（CURRENCY_SYMBOLS），此处仅保留本地格式化函数。

// 获取货币符号
const getCurrencySymbol = (currency) => {
    return CURRENCY_SYMBOLS[currency?.toUpperCase()] || currency || "$";
};

// 添加CSS样式到页面头部
const injectEditableStyles = () => {
    if (document.getElementById("editable-styles")) return;

    const style = document.createElement("style");
    style.id = "editable-styles";
    style.textContent = `
        .editable-row:hover {
            background-color: #fafafa !important;
        }
        .editable-cell-value-wrap {
            padding: 4px 11px;
            cursor: pointer;
            min-height: 32px;
            display: flex;
            align-items: center;
            justify-content: space-between;
        }
        .editable-cell-value-wrap:hover {
            background-color: #f5f5f5;
        }
        .ant-table-tbody > tr.editable-row > td {
            padding: 4px !important;
        }
        
        /* 数字列右对齐 - 显示状态 */
        .ant-table-tbody .ant-table-cell[style*="text-align: right"] .editable-cell-value-wrap {
            justify-content: flex-end !important;
        }

        /* 选中行样式 */
        .ant-table-tbody > tr.selected-row > td {
            background-color: #e6f7ff !important;
            border-top: 2px solid #1890ff !important;
            border-bottom: 2px solid #1890ff !important;
            position: relative;
            z-index: 2;
        }
        .ant-table-tbody > tr.selected-row > td:first-child {
            border-left: 2px solid #1890ff !important;
        }
        .ant-table-tbody > tr.selected-row > td:last-child {
            border-right: 2px solid #1890ff !important;
        }
        .ant-table-tbody > tr.voided-line-item-row > td {
            background-color: #fafafa !important;
            color: #8c8c8c;
        }
        .ant-table-tbody > tr.voided-line-item-row > td:not(:last-child) {
            text-decoration: line-through;
        }
        .ant-table-tbody > tr.voided-line-item-row .ant-tag {
            opacity: 0.65;
        }
    `;
    document.head.appendChild(style);
};

// 对比状态定义
const COMPARE_STATUS = {
    MATCH: "match", // 完全匹配
    DIFF: "diff", // 有差异
    WARNING: "warning", // 缺少expected值
    MISSING: "missing", // 缺少实际值
};

const UNIT_PRICE_EDIT_PRECISION = 12;

const getCountComparison = (actualValue, expectedValue) => {
    if (expectedValue === undefined || expectedValue === null) {
        return {
            status: COMPARE_STATUS.WARNING,
            color: "#faad14",
            icon: <InfoCircleOutlined />,
            tooltip: "缺少AI期望值用于对比",
        };
    }

    if (actualValue === undefined || actualValue === null) {
        return {
            status: COMPARE_STATUS.MISSING,
            color: "#ff4d4f",
            icon: <ExclamationCircleOutlined />,
            tooltip: "缺少实际计算值",
        };
    }

    if (Number(expectedValue) === Number(actualValue)) {
        return {
            status: COMPARE_STATUS.MATCH,
            color: "#52c41a",
            icon: <CheckCircleOutlined />,
            tooltip: `期望项目数与实际项目数一致: ${Number(expectedValue)}`,
        };
    }

    return {
        status: COMPARE_STATUS.DIFF,
        color: "#ff4d4f",
        icon: <ExclamationCircleOutlined />,
        tooltip: `期望项目数: ${Number(expectedValue)}，实际项目数: ${Number(actualValue)}`,
    };
};

// 小计是否平衡由后端统一金额内核判定，前端不重复实现容差策略。
const getAmountComparison = (actualValue, expectedValue, balanceStatus) => {
    if (expectedValue === undefined || expectedValue === null) {
        return {
            status: COMPARE_STATUS.WARNING,
            color: "#faad14",
            icon: <InfoCircleOutlined />,
            tooltip: "缺少AI期望值用于对比",
        };
    }

    if (actualValue === undefined || actualValue === null) {
        return {
            status: COMPARE_STATUS.MISSING,
            color: "#ff4d4f",
            icon: <ExclamationCircleOutlined />,
            tooltip: "缺少实际计算值",
        };
    }

    const expectedLabel = Number(expectedValue).toLocaleString(undefined, { minimumFractionDigits: 2 });
    const actualLabel = Number(actualValue).toLocaleString(undefined, { minimumFractionDigits: 2 });
    if (balanceStatus === INVOICE_BALANCE_STATUS.NATURALLY_BALANCED) {
        return {
            status: COMPARE_STATUS.MATCH,
            color: "#52c41a",
            icon: <CheckCircleOutlined />,
            tooltip: `小计校验已通过。期望值: ${expectedLabel}，实际值: ${actualLabel}`,
        };
    }

    if (balanceStatus === INVOICE_BALANCE_STATUS.UNEXPLAINED_VARIANCE) {
        return {
            status: COMPARE_STATUS.DIFF,
            color: "#ff4d4f",
            icon: <ExclamationCircleOutlined />,
            tooltip: `金额不匹配，请检查数据。期望值: ${expectedLabel}，实际值: ${actualLabel}`,
        };
    }

    return {
        status: COMPARE_STATUS.WARNING,
        color: "#faad14",
        icon: <InfoCircleOutlined />,
        tooltip: "小计校验状态待后端返回",
    };
};

const InvoiceAmountDisplay = ({ summary }) => {
    if (!summary) {
        return <Text type="secondary">金额摘要待后端返回</Text>;
    }

    const expectedTotal = Number(summary.expected_total);
    const computedTotal = Number(summary.computed_total);
    const hasExpectedTotal = summary.expected_total !== null
        && summary.expected_total !== undefined
        && Number.isFinite(expectedTotal);
    const displayAmount = hasExpectedTotal ? expectedTotal : computedTotal;
    const presentation = getInvoiceBalancePresentation(summary);
    const tooltip = [
        hasExpectedTotal ? `发票声明总额: ${formatSignedAmount(expectedTotal).replace(/^\+/, "")}` : null,
        Number.isFinite(computedTotal) ? `明细声明合计: ${formatSignedAmount(computedTotal).replace(/^\+/, "")}` : null,
        presentation.annotation,
        ...(summary.warnings || []).slice(0, 3).map((warning) => warning.message || warning.code),
    ].filter(Boolean).join("\n");

    return (
        <Tooltip title={tooltip} overlayStyle={{ whiteSpace: "pre-line" }}>
            <div>
                <Statistic
                    title={hasExpectedTotal ? "发票声明总额" : "明细声明合计"}
                    value={Number.isFinite(displayAmount) ? roundMoney(displayAmount) : "-"}
                    precision={2}
                    prefix={summary.currency ? `${summary.currency} ` : undefined}
                    valueStyle={{
                        color: presentation.color,
                        fontWeight: presentation.status === INVOICE_BALANCE_STATUS.UNEXPLAINED_VARIANCE ? 600 : 400,
                    }}
                />
                {presentation.annotation && (
                    <Text style={{ display: "block", color: presentation.color, fontSize: 11, marginTop: -4 }}>
                        {presentation.annotation}
                    </Text>
                )}
            </div>
        </Tooltip>
    );
};

const InvoiceBalanceAlert = ({ summary }) => {
    if (summary?.balance_status !== INVOICE_BALANCE_STATUS.UNEXPLAINED_VARIANCE) return null;

    const mismatchedSectionIds = (Array.isArray(summary.section_balance) ? summary.section_balance : [])
        .filter((item) => item?.section_balance_status === INVOICE_BALANCE_STATUS.UNEXPLAINED_VARIANCE)
        .map((item) => item.section_id)
        .filter((sectionId) => sectionId !== undefined && sectionId !== null && sectionId !== "");
    const variance = summary.unexplained_variance ?? summary.header_variance;
    const details = [];
    if (variance !== null && variance !== undefined && variance !== "" && Number.isFinite(Number(variance)) && Number(variance) !== 0) {
        details.push(`发票总额差异 ${formatSignedAmount(variance)}`);
    }
    if (mismatchedSectionIds.length > 0) {
        details.push(`小计不匹配分节：${mismatchedSectionIds.join("、")}`);
    }

    return (
        <Alert
            type="error"
            showIcon
            message="金额不匹配，请检查数据"
            description={`${details.length > 0 ? `${details.join("；")}。` : ""}请修正明细数据或重试解析。`}
            style={{ marginTop: 12 }}
        />
    );
};

// 可编辑的账单头字段组件
const EditableHeaderField = ({ value, onChange, fieldPath, component: Component = Input, ...props }) => {
    const { reconciliationState, restoreInvoiceMetaField } = useInvoiceReview();
    const dirtyFields = reconciliationState?.dirtyFields || new Set();
    const isModified = dirtyFields.has(fieldPath);

    return (
        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <div style={{ flex: 1, position: "relative" }}>
                <Component
                    value={value}
                    onChange={onChange}
                    style={{
                        ...props.style,
                        backgroundColor: isModified ? "#fff7e6" : undefined,
                        borderColor: isModified ? "#faad14" : undefined,
                        transition: "all 0.3s",
                    }}
                    {...props}
                />
            </div>
            {isModified && (
                <Tooltip title="已修改 - 点击还原">
                    <Button
                        type="text"
                        size="small"
                        icon={<UndoOutlined style={{ color: "#faad14" }} />}
                        onClick={(e) => {
                            e.stopPropagation();
                            restoreInvoiceMetaField(fieldPath);
                            message.success("已还原字段值");
                        }}
                        style={{ marginLeft: 4 }}
                    />
                </Tooltip>
            )}
        </div>
    );
};

// 费用明细单元格显示与编辑组件
// 计算逻辑：数量和总价是主输入，单价自动计算 (unit_price = total_price / quantity)
const FeeDetailsCell = ({ record, onSave, currencySymbol, editable, isModified }) => {
    const [open, setOpen] = useState(false);
    const [form] = Form.useForm();

    // 监听数量和总价变化，自动计算单价
    const watchedQuantity = Form.useWatch("quantity", form);
    const watchedTotalPrice = Form.useWatch("total_price", form);

    // 自动计算单价：当数量或总价变化时触发
    React.useEffect(() => {
        if (!open) return; // 仅在弹窗打开时计算
        const qty = Number(watchedQuantity);
        const total = Number(watchedTotalPrice);
        if (qty && !isNaN(qty) && qty !== 0 && !isNaN(total)) {
            const computed = Number((total / qty).toFixed(UNIT_PRICE_EDIT_PRECISION));
            form.setFieldValue("unit_price", computed);
        }
    }, [watchedQuantity, watchedTotalPrice, open, form]);

    const handleOpenChange = (newOpen) => {
        if (!editable) return;
        if (newOpen) {
            form.setFieldsValue({
                quantity: record.quantity,
                unit_price: record.unit_price,
                total_price: record.total_price,
            });
        }
        setOpen(newOpen);
    };

    const handleSave = () => {
        form.validateFields().then((values) => {
            // 提交时确保单价基于最新的数量和总价计算，避免残留旧值
            const qty = Number(values.quantity);
            const total = Number(values.total_price);
            if (qty && qty !== 0 && !isNaN(total)) {
                values.unit_price = Number((total / qty).toFixed(UNIT_PRICE_EDIT_PRECISION));
            }
            onSave(values);
            setOpen(false);
        });
    };

    const qty = record.quantity !== undefined && record.quantity !== null ? Number(record.quantity).toFixed(2) : "-";
    const qtyWithUnit = record.unit ? `${qty} ${record.unit}` : qty;
    const unitPrice = record.unit_price !== undefined && record.unit_price !== null
        ? Number(record.unit_price).toFixed(UNIT_PRICE_EDIT_PRECISION)
        : "-";
    const totalPrice = record.total_price !== undefined && record.total_price !== null ? Number(record.total_price).toLocaleString(undefined, { minimumFractionDigits: 2 }) : "-";

    const content = (
        <div style={{ padding: 8, width: 280 }} onClick={(e) => e.stopPropagation()}>
            <Form form={form} layout="vertical">
                <Row gutter={8}>
                    <Col span={12}>
                        <Form.Item name="quantity" label="数量" style={{ marginBottom: 8 }}>
                            <InputNumber style={{ width: "100%" }} min={0} onPressEnter={handleSave} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="total_price" label="总价" style={{ marginBottom: 8 }}>
                            <InputNumber style={{ width: "100%" }} precision={2} onPressEnter={handleSave} />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="unit_price" label={<span>单价 <span style={{ fontSize: 11, color: "#8c8c8c", fontWeight: "normal" }}>（自动计算）</span></span>} style={{ marginBottom: 16 }}>
                    <InputNumber style={{ width: "100%", background: "#fafafa" }} precision={UNIT_PRICE_EDIT_PRECISION} readOnly />
                </Form.Item>
                <div style={{ textAlign: "right" }}>
                    <Space>
                        <Button size="small" onClick={() => setOpen(false)}>
                            取消
                        </Button>
                        <Button type="primary" size="small" onClick={handleSave}>
                            确定
                        </Button>
                    </Space>
                </div>
            </Form>
        </div>
    );

    return (
        <Popover content={content} title="编辑费用明细" trigger="click" open={open} onOpenChange={handleOpenChange}>
            <div
                style={{
                    cursor: editable ? "pointer" : "default",
                    textAlign: "right",
                    backgroundColor: isModified ? "#fff7e6" : "transparent",
                    padding: "4px",
                    borderRadius: "2px",
                    transition: "background-color 0.3s",
                }}
            >
                <div style={{ fontSize: "12px", color: "#8c8c8c", lineHeight: "1.4" }}>
                    {qtyWithUnit} <span style={{ fontSize: "10px", margin: "0 2px" }}>×</span>
                    <Tooltip title={record.unit_price} placement="topRight">
                        <span style={{ borderBottom: "1px dashed #d9d9d9" }}>
                            {currencySymbol}
                            {unitPrice}
                        </span>
                    </Tooltip>
                </div>
                <div style={{ fontWeight: 500, fontSize: "14px", color: "#262626" }}>
                    {currencySymbol} {totalPrice}
                </div>
            </div>
        </Popover>
    );
};

const ReconciliationWorkspace = () => {
    const { reconciliationState, updateReconciliationData, invoices, selectLineItem, clearSelectedLineItem, agentData, addSection, removeSection, addLineItem, removeLineItem, restoreLineItem, selectedLineItem, invoiceDataSourceId, invoiceDataSource, startPolling, validationData, refresh, contractMatchResult, threeTierReadiness, dataSourceStatus } = useInvoiceReview();
    const { airports: airportsMap } = useAirports(); // 获取机场数据用于时区转换
    const [viewFilter, setViewFilter] = useState("all"); // 'all', 'problems_only', 'user_modified'
    const [expandedSections, setExpandedSections] = useState({});
    const [showAgentAnalysis, setShowAgentAnalysis] = useState(false);
    // 表格修正 Fixer 功能状态
    const [aiFixModal, setAiFixModal] = useState({
        visible: false,
        invoiceIndex: null,
        sectionIdentifier: null,
        loading: false,
    });
    const [aiFixFeedback, setAiFixFeedback] = useState("");

    // 路由相关代码已移除 — 纯嵌入式组件
    const hasUnsavedChanges = reconciliationState.hasUnsavedChanges;
    // 终态账单不可继续执行会修改账单数据的 Fixer 操作。
    const isReadOnly = idsIsTerminal(dataSourceStatus);

    const getLineState = useCallback((item, invoiceIndex, sectionIndex, itemIndex) => {
        const dirtyFields = reconciliationState?.dirtyFields || new Set();
        const fieldPath = lineItemFieldPath(invoiceIndex, sectionIndex, itemIndex);
        const modified = isLineItemModified(dirtyFields, fieldPath);
        const validationStale = isLineItemValidationStale(dirtyFields, fieldPath);
        const currentAnomalies = validationStale ? [] : (item?.anomalies || []);
        const matchedFlightIds = Array.isArray(item?.matched_flight_ids) ? item.matched_flight_ids : [];
        const hasNoFlight = matchedFlightIds.length === 0;
        return {
            fieldPath,
            modified,
            validationStale,
            currentAnomalies,
            hasNoFlight,
            hasCurrentProblem: currentAnomalies.length > 0 || hasNoFlight,
        };
    }, [reconciliationState?.dirtyFields]);

    // 表格修正 Fixer 弹窗打开
    const openAiFixModal = (invoiceIndex, sectionIdentifier) => {
        if (isReadOnly) {
            message.warning("当前状态不可修改");
            return;
        }

        setAiFixModal({
            visible: true,
            invoiceIndex,
            sectionIdentifier,
            loading: false,
        });
        setAiFixFeedback("");
    };

    // 表格修正 Fixer 弹窗关闭
    const closeAiFixModal = () => {
        setAiFixModal({
            visible: false,
            invoiceIndex: null,
            sectionIdentifier: null,
            loading: false,
        });
        setAiFixFeedback("");
    };

    // 执行表格修正 Fixer（异步模式 - 提交后立即返回）
    const handleAiFix = async () => {
        if (isReadOnly) {
            message.warning("当前状态不可修改");
            return;
        }

        if (!aiFixFeedback.trim()) {
            message.warning("请输入修正描述");
            return;
        }

        if (!invoiceDataSourceId) {
            message.error("缺少账单数据源 ID，无法执行表格修正 Fixer");
            return;
        }

        setAiFixModal((prev) => ({ ...prev, loading: true }));

        try {
            const settlementApi = (await import("../../../../../api/settlementApi")).default;
            const response = await settlementApi.fixAPInvoice({
                invoice_data_source_id: invoiceDataSourceId,
                invoice_index: aiFixModal.invoiceIndex,
                section_identifier: aiFixModal.sectionIdentifier,
                user_feedback: aiFixFeedback,
            });

            if (response.data?.success) {
                // 关闭弹窗
                closeAiFixModal();
                // 提示用户任务已提交
                message.success("表格修正 Fixer 任务已提交，正在处理中...");
                // 使用 Context 的 startPolling 开始轮询（与阶段重跑共享机制）
                startPolling("表格修正 Fixer 执行中...");
            } else {
                message.error(response.data?.error || response.data?.message || "表格修正 Fixer 失败");
            }
        } catch (error) {
            console.error("表格修正 Fixer 失败:", error);
            message.error(error.response?.data?.detail || error.message || "表格修正 Fixer 失败");
        } finally {
            setAiFixModal((prev) => ({ ...prev, loading: false }));
        }
    };

    // 组件挂载时注入样式
    React.useEffect(() => {
        injectEditableStyles();
    }, []);

    // 初始化展开所有面板
    React.useEffect(() => {
        if (invoices && invoices.length > 0) {
            const newExpandedSections = {};
            invoices.forEach((invoice, invoiceIndex) => {
                if (invoice.sections) {
                    // 为每个账单创建独立的展开状态
                    newExpandedSections[invoiceIndex] = invoice.sections.map((_, sectionIndex) => `${invoiceIndex}-${sectionIndex}`);
                }
            });
            setExpandedSections(newExpandedSections);
        }
    }, [invoices]);

    // 从原始路径中解析 objectKey
    const resolveObjectKey = (raw) => {
        if (!raw) return null;
        if (typeof raw === "string" && /^https?:\/\//i.test(raw)) {
            try {
                const u = new URL(raw);
                return decodeURIComponent(u.pathname.replace(/^\//, ""));
            } catch (e) {
                return raw.replace(/^\//, "");
            }
        }
        return String(raw).replace(/^\//, "");
    };

    // 打开单个文件的预签名链接
    const openSingleFile = async (rawPath, fallbackLabel) => {
        const objectKey = resolveObjectKey(rawPath);
        if (!objectKey) {
            message.warning(`无法解析文件路径${fallbackLabel ? `: ${fallbackLabel}` : ""}`);
            return;
        }
        try {
            const data = await getPresignedViewUrl(objectKey);
            if (data?.signed_url) {
                window.open(data.signed_url, "_blank", "noopener,noreferrer");
            } else {
                throw new Error("未返回预签名链接");
            }
        } catch (error) {
            console.warn("获取预签名链接失败:", error);
            if (rawPath && /^https?:\/\//i.test(rawPath)) {
                window.open(rawPath, "_blank", "noopener,noreferrer");
            } else {
                message.error(`无法打开文件${fallbackLabel ? `: ${fallbackLabel}` : ""}`);
            }
        }
    };

    // 查看原文件（通过预签名链接），支持单文件和 FILE_GROUP 多文件
    const handleViewOriginalFile = async (sourceFile) => {
        if (!sourceFile) return;

        // FILE_GROUP：包含多个实际文件，逐一打开
        if (sourceFile.record_type === "FILE_GROUP" && sourceFile.group_files?.length > 0) {
            for (const gf of sourceFile.group_files) {
                await openSingleFile(gf.object_key, gf.filename);
            }
            return;
        }

        // 单文件模式
        if (!sourceFile.path) {
            message.warning("未找到原文件路径");
            return;
        }
        await openSingleFile(sourceFile.path, sourceFile.filename);
    };

    // 处理单元格保存
    const handleCellSave = (record, invoiceIndex, sectionIndex, itemIndex) => {
        return (updatedRecord) => {
            const fieldPath = `invoices[${invoiceIndex}].sections[${sectionIndex}].line_items[${itemIndex}]`;

            // 检查哪些字段发生了变化
            Object.keys(updatedRecord).forEach((key) => {
                if (updatedRecord[key] !== record[key]) {
                    const fullFieldPath = `${fieldPath}.${key}`;
                    updateReconciliationData(fullFieldPath, updatedRecord[key]);
                }
            });
        };
    };

    // 获取汇率转换后的金额
    const convertCurrency = (amount, fromCurrency, toCurrency, exchangeRates) => {
        if (fromCurrency === toCurrency || !exchangeRates || Object.keys(exchangeRates).length === 0) {
            return amount;
        }

        // 查找对应的汇率
        const rateKey = `${fromCurrency.toLowerCase()}_to_${toCurrency.toLowerCase()}`;
        const rate = exchangeRates[rateKey];

        if (rate && typeof rate === "number") {
            return amount * rate;
        }

        console.warn(`未找到汇率 ${rateKey}，使用原币种金额`);
        return amount;
    };

    // 从同一账单的其他 section 中查找相同币种的汇率（回退逻辑）
    const findExchangeRateFromOtherSections = (targetCurrency, invoiceCurrency, allSections, currentSection) => {
        if (!allSections || allSections.length === 0) return null;
        
        const rateKey = `${targetCurrency.toLowerCase()}_to_${invoiceCurrency.toLowerCase()}`;
        
        for (const section of allSections) {
            // 跳过当前 section
            if (section === currentSection) continue;
            
            // 检查币种是否匹配
            if (section.currency === targetCurrency) {
                const rate = section.additional_info?.exchange_rate?.[rateKey];
                if (rate && typeof rate === "number") {
                    console.info(`[汇率回退] 从 section "${section.section_identifier}" 获取汇率: ${rateKey} = ${rate}`);
                    return rate;
                }
            }
        }
        return null;
    };

    // 计算每个分节的小计金额（返回原币种和转换后的金额）
    // allSections 用于在缺少汇率时从其他相同币种的 section 查找
    const calculateSectionSubtotal = (section, invoiceCurrency = "USD", allSections = []) => {
        if (!section.line_items || section.line_items.length === 0) {
            return { original: 0, converted: 0 };
        }

        // 先量化到分再返回，避免浮点累加尾差（如 6737.969999999999）扩散到展示层
        const originalTotal = sumMoney(section.line_items.map((item) => item.total_price));

        // 如果小节有独立币种且与账单主币种不同，进行汇率转换
        const sectionCurrency = section.currency || invoiceCurrency;
        let exchangeRates = section.additional_info?.exchange_rate;

        // 回退逻辑：如果当前 section 没有汇率，尝试从其他相同币种的 section 获取
        if (sectionCurrency !== invoiceCurrency && (!exchangeRates || Object.keys(exchangeRates).length === 0)) {
            const fallbackRate = findExchangeRateFromOtherSections(sectionCurrency, invoiceCurrency, allSections, section);
            if (fallbackRate) {
                exchangeRates = { [`${sectionCurrency.toLowerCase()}_to_${invoiceCurrency.toLowerCase()}`]: fallbackRate };
            }
        }

        const convertedTotal = sectionCurrency !== invoiceCurrency ? roundMoney(convertCurrency(originalTotal, sectionCurrency, invoiceCurrency, exchangeRates)) : originalTotal;

        return {
            original: originalTotal,
            converted: convertedTotal,
            currency: sectionCurrency,
        };
    };

    // 生成动态的行项目表格列定义
    const generateLineItemColumns = (invoiceIndex, sectionIndex) => {
        // 获取当前账单的 product_map（用于 product_code -> name 映射）
        const currentInvoice = invoices?.[invoiceIndex];
        const productMap = currentInvoice?.meta?.product_map || {};
        // 聚合同发票全部 sections 的 line_items，供关联航班悬浮提示的分摊信息计算复用
        const invoiceLineItems = (currentInvoice?.sections || []).flatMap((s) => s.line_items || []);

        const baseColumns = [
            {
                title: "序号",
                dataIndex: "index",
                key: "index",
                width: 40,
                align: "center",
                render: (_, record, index) => index + 1,
            },
            {
                title: "产品",
                dataIndex: "product_code",
                key: "product_code",
                width: 150,
                editable: false,
                render: (productCode) => {
                    // 无产品编码
                    if (!productCode) return <Tag color="default">-</Tag>;

                    // 从 product_map 中获取产品信息 (V3.0: 结构为 {name, cost_type})
                    const productInfo = productMap[productCode];
                    const productName = productInfo?.name || productInfo; // 兼容旧结构

                    // 未在产品库中找到匹配 → 警告状态
                    if (!productName) {
                        return (
                            <Tooltip
                                title={
                                    <div>
                                        <div style={{ fontWeight: "bold", color: "#faad14" }}>
                                            <ExclamationCircleOutlined /> 未识别的产品
                                        </div>
                                        <div>编码: {productCode}</div>
                                        <div style={{ fontSize: 12, marginTop: 4 }}>请检查产品库或手动确认</div>
                                    </div>
                                }
                                placement="topLeft"
                            >
                                <Tag color="warning" style={{ cursor: "pointer" }}>
                                    <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                                    {productCode}
                                </Tag>
                            </Tooltip>
                        );
                    }

                    // 正常匹配 → 显示产品名称
                    const isNewProduct = productInfo?.is_new === true;
                    return (
                        <Tooltip title={<div><div>编码: {productCode}</div>{isNewProduct && <div style={{ color: "#52c41a", fontSize: 11, marginTop: 2 }}>✦ 本次 AI 新发现的产品</div>}</div>} placement="topLeft">
                            <span style={{ fontWeight: 500 }}>
                                {productName}
                                {isNewProduct && <sup style={{ color: "#52c41a", fontSize: 10, marginLeft: 2, fontWeight: 600 }}>NEW</sup>}
                            </span>
                        </Tooltip>
                    );
                },
            },
            {
                title: "描述",
                dataIndex: "description",
                key: "description",
                width: 200,
                editable: true,
                render: (text) => (
                    <Tooltip title={text || ""} placement="topLeft">
                        <div
                            style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                lineHeight: "1.5",
                                maxHeight: "3em",
                                cursor: "pointer",
                            }}
                        >
                            {text || "-"}
                        </div>
                    </Tooltip>
                ),
            },
            {
                title: "费用明细",
                key: "fee_details",
                width: 160,
                align: "right",
                render: (_, record, index) => {
                    const sectionCurrency = record.sectionCurrency || "USD";
                    const currencySymbol = getCurrencySymbol(sectionCurrency);

                    // 获取索引和修改状态
                    const dirtyFields = reconciliationState?.dirtyFields || new Set();
                    const itemIndex = record.itemIndex !== undefined ? record.itemIndex : index;
                    const baseFieldPath = `invoices[${invoiceIndex}].sections[${sectionIndex}].line_items[${itemIndex}]`;

                    // 检查相关字段是否被修改
                    const isQtyModified = dirtyFields.has(`${baseFieldPath}.quantity`);
                    const isPriceModified = dirtyFields.has(`${baseFieldPath}.unit_price`);
                    const isTotalModified = dirtyFields.has(`${baseFieldPath}.total_price`);
                    const isModified = isQtyModified || isPriceModified || isTotalModified;

                    // 使用闭包捕获当前行信息
                    const handleSave = (values) => {
                        const saver = handleCellSave(record, invoiceIndex, sectionIndex, index);
                        saver(values);
                    };

                    return <FeeDetailsCell record={record} onSave={handleSave} currencySymbol={currencySymbol} editable={isLineItemActive(record)} isModified={isModified} />;
                },
            },
            {
                title: "费用类型",
                dataIndex: "cost_type",
                key: "cost_type",
                width: 42,
                editable: true,
                inputType: "select",
                selectOptions: STANDARD_COST_TYPE_OPTIONS,
                render: (value, record) => {
                    const resolution = resolveLineItemCostType({
                        item: { ...record, cost_type: value },
                        invoiceMeta: currentInvoice?.meta || {},
                        invoiceDataSource,
                    });
                    const { costType, sourceLabel } = resolution;
                    if (!costType) {
                        return <Tag color="default">未设置</Tag>;
                    }
                    const costTypeTag = (
                        <Tooltip title={`来源：${sourceLabel}`}>
                            <Tag color="blue">{getStandardCostTypeLabel(costType)}</Tag>
                        </Tooltip>
                    );
                    // 已入库账单显示冻结值；若当前产品档案解析结果与冻结值不一致，标记提示
                    if (resolution.source === "committed_line_item" && resolution.isStaleAgainstCurrent) {
                        return (
                            <Space size={4}>
                                {costTypeTag}
                                <Tooltip
                                    title={
                                        `入库时为「${getStandardCostTypeLabel(costType)}」，` +
                                        `当前产品档案解析为「${getStandardCostTypeLabel(resolution.currentCostType)}」。` +
                                        `如需按新档案生效，请对该账单执行作废并重发。`
                                    }
                                >
                                    <ExclamationCircleOutlined style={{ color: "#faad14" }} />
                                </Tooltip>
                            </Space>
                        );
                    }
                    return costTypeTag;
                },
            },
            {
                title: "分摊类型",
                dataIndex: "allocation_type",
                key: "allocation_type",
                width: 52,
                editable: false,
                render: (value) => {
                    if (value && ALLOCATION_TYPE_LABELS[value]) {
                        return <Tag color={ALLOCATION_TYPE_COLORS[value]}>{ALLOCATION_TYPE_LABELS[value]}</Tag>;
                    }
                    return "-";
                },
            },
            {
                title: "分摊规则",
                dataIndex: "allocation_rule",
                key: "allocation_rule",
                width: 52,
                editable: false,
                render: (value) => {
                    if (value) {
                        // 优先展示精确中文名；未知规则值 fallback 到原始字符串（doc 70 §6.1）
                        return <Tag color="cyan">{ALLOCATION_RULE_LABELS[value] || value}</Tag>;
                    }
                    return "-";
                },
            },
            {
                title: "关联航班",
                dataIndex: "matched_flight_ids",
                key: "matched_flight_ids",
                width: 160, // 增加宽度以容纳校验状态
                editable: false,
                render: (_, record) => {
                    // 从 Context 获取的 validationData 中提取 flight_results
                    const flightResults = validationData?.validation_result?.flight_results || validationData?.aggregated_result?.flight_results || {};
                    const hasValidationData = validationData?.has_validation || false;

                    // 判断该行项目是否被用户修改过
                    const itemIndex = record.itemIndex !== undefined ? record.itemIndex : record.key?.split("-").pop();
                    const lineState = getLineState(record, invoiceIndex, sectionIndex, itemIndex);
                    const costTypeResolution = resolveLineItemCostType({
                        item: record,
                        invoiceMeta: currentInvoice?.meta || {},
                        invoiceDataSource,
                    });

                    return (
                        <FlightValidationCell
                            record={record}
                            flightResults={flightResults}
                            airportsMap={airportsMap}
                            hasValidationData={hasValidationData}
                            isModified={lineState.modified}
                            validationStale={lineState.validationStale}
                            lineItemProductCode={resolveLineItemProductCode(record)}
                            lineItemCostType={costTypeResolution.costType}
                            invoiceLineItems={invoiceLineItems}
                            productMap={productMap}
                            onOpenDrawer={() => selectLineItem(invoiceIndex, sectionIndex, record.itemIndex, "flight_matching")}
                        />
                    );
                },
            },
            {
                title: "状态",
                key: "status",
                width: 40,
                editable: false,
                render: (_, record) => {
                    const itemIndex = record.itemIndex !== undefined ? record.itemIndex : record.key.split("-").pop();
                    const lineState = getLineState(record, invoiceIndex, sectionIndex, itemIndex);
                    const lifecycleStatus = getLineItemLifecycleStatus(record);

                    if (lifecycleStatus !== "ACTIVE") {
                        const statusLabel = lifecycleStatus === "REPLACED" ? "已替代" : "已作废";
                        return (
                            <Tooltip title={getVoidReason(record) || statusLabel}>
                                <Tag color={lifecycleStatus === "REPLACED" ? "orange" : "default"}>{statusLabel}</Tag>
                            </Tooltip>
                        );
                    }

                    if (lineState.validationStale && record.matched_flight_ids?.length > 0) {
                        return (
                            <Tooltip title="该行已被手动修改，旧验证航班结果不再代表当前事实，请保存后重新验证航班">
                                <Tag color="blue">待重验</Tag>
                            </Tooltip>
                        );
                    }
                    if (lineState.currentAnomalies.length > 0) {
                        return (
                            <Tooltip title="需要复核">
                                <ExclamationCircleOutlined style={{ color: "#faad14", fontSize: 16 }} />
                            </Tooltip>
                        );
                    }
                    if (lineState.modified) {
                        return (
                            <Tooltip title="用户已修改">
                                <EditOutlined style={{ color: "#1890ff", fontSize: 16 }} />
                            </Tooltip>
                        );
                    }
                    if (record.matched_flight_ids?.length > 0) {
                        return <Tag color="green">已匹配</Tag>;
                    }
                    return <Tag color="orange">待分配</Tag>;
                },
            },
            {
                title: "备注",
                dataIndex: "remarks",
                key: "remarks",
                width: 160,
                editable: true,
                render: (text) => (
                    <Tooltip title={text || "点击添加行备注"} placement="topLeft">
                        <Text type={text ? undefined : "secondary"} ellipsis style={{ maxWidth: 140 }}>
                            {text || "-"}
                        </Text>
                    </Tooltip>
                ),
            },
            {
                title: "操作",
                key: "action",
                width: 160,
                align: "center",
                render: (_, record, index) => {
                    // 检查该行项目是否被修改
                    const dirtyFields = reconciliationState?.dirtyFields || new Set();
                    const fieldPath = lineItemFieldPath(invoiceIndex, sectionIndex, index);
                    const isModified = isLineItemModified(dirtyFields, fieldPath);
                    const isActive = isLineItemActive(record);

                    return (
                        <Space size="small">
                            {isModified && (
                                <Tooltip title="还原到AI建议">
                                    <Button
                                        type="text"
                                        icon={<UndoOutlined />}
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            restoreLineItem(invoiceIndex, sectionIndex, index);
                                            message.success("已还原到AI原始建议");
                                        }}
                                    />
                                </Tooltip>
                            )}
                            <Popconfirm
                                title="确定删除此行项目吗？"
                                onConfirm={(e) => {
                                    e?.stopPropagation();
                                    removeLineItem(invoiceIndex, sectionIndex, index);
                                }}
                                onCancel={(e) => e?.stopPropagation()}
                                okText="确定"
                                cancelText="取消"
                                disabled={!isActive}
                            >
                                <Button type="text" danger icon={<DeleteOutlined />} size="small" disabled={!isActive} onClick={(e) => e.stopPropagation()} />
                            </Popconfirm>
                        </Space>
                    );
                },
            },
        ];

        return baseColumns.map((col) => {
            if (!col.editable) {
                return col;
            }
            return {
                ...col,
                onCell: (record, index) => ({
                    record,
                    editable: col.editable && isLineItemActive(record),
                    dataIndex: col.dataIndex,
                    title: col.title,
                    handleSave: handleCellSave(record, invoiceIndex, sectionIndex, index),
                    inputType: col.inputType,
                    selectOptions: col.selectOptions || [],
                    precision: col.precision, // 传递precision属性
                    currency: record.sectionCurrency, // 传递币种信息
                }),
            };
        });
    };

    // 渲染单个账单的内容
    const renderInvoiceContent = (invoice, invoiceIndex) => {
        const invoiceMeta = invoice.meta;
        const sections = invoice.sections;
        const amountSummary = invoice.invoice_summary;
        const sectionBalances = Array.isArray(amountSummary?.section_balance)
            ? amountSummary.section_balance
            : [];

        return (
            <div>
                <Badge.Ribbon text={`#${invoiceIndex + 1}`} color="cyan" placement="start">
                    <Card size="small" style={{ marginBottom: 12, borderTop: "2px solid #13c2c2" }} loading={reconciliationState.status === "LOADING"}>
                        {invoiceMeta ? (
                            <>
                                <Row gutter={16} align="middle">
                                    <Col span={6}>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <Text type="secondary" style={{ fontSize: 12, width: 48, flexShrink: 0 }}>
                                                账单号
                                            </Text>
                                            <div style={{ flex: 1 }}>
                                                <EditableHeaderField
                                                    value={invoiceMeta.invoice_number}
                                                    onChange={(e) => updateReconciliationData(`invoices[${invoiceIndex}].meta.invoice_number`, e.target.value)}
                                                    fieldPath={`invoices[${invoiceIndex}].meta.invoice_number`}
                                                    bordered={false}
                                                    style={{ borderBottom: "1px solid #d9d9d9", borderRadius: 0, padding: "4px 0" }}
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={6}>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <Text type="secondary" style={{ fontSize: 12, width: 48, flexShrink: 0 }}>
                                                供应商
                                            </Text>
                                            <div style={{ flex: 1, borderBottom: "1px solid #d9d9d9" }}>
                                                <EditableHeaderField
                                                    component={SupplierSelector}
                                                    value={invoiceMeta.supplier_id}
                                                    onChange={(val, opt) => {
                                                        updateReconciliationData(`invoices[${invoiceIndex}].meta.supplier_id`, val);
                                                        updateReconciliationData(`invoices[${invoiceIndex}].meta.supplier_name`, opt?.supplier?.name);
                                                    }}
                                                    fieldPath={`invoices[${invoiceIndex}].meta.supplier_id`}
                                                    bordered={false}
                                                    style={{ width: "100%" }}
                                                    placeholder={invoiceMeta.supplier_name || "选择供应商"}
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={4}>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <Tooltip title="账单日期（Local Time）">
                                                <Text type="secondary" style={{ fontSize: 12, width: 70, flexShrink: 0 }}>
                                                    日期(LT)
                                                </Text>
                                            </Tooltip>
                                            <div style={{ flex: 1 }}>
                                                <EditableHeaderField
                                                    component={DatePicker}
                                                    value={invoiceMeta.invoice_date ? dayjs(invoiceMeta.invoice_date) : null}
                                                    onChange={(date, dateString) => updateReconciliationData(`invoices[${invoiceIndex}].meta.invoice_date`, dateString)}
                                                    fieldPath={`invoices[${invoiceIndex}].meta.invoice_date`}
                                                    bordered={false}
                                                    style={{ width: "100%", borderBottom: "1px solid #d9d9d9", borderRadius: 0 }}
                                                />
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={3}>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <Text type="secondary" style={{ fontSize: 12, width: 48, flexShrink: 0 }}>
                                                币种
                                            </Text>
                                            <div style={{ flex: 1 }}>
                                                <EditableHeaderField component={Select} value={invoiceMeta.currency} onChange={(val) => updateReconciliationData(`invoices[${invoiceIndex}].meta.currency`, val)} fieldPath={`invoices[${invoiceIndex}].meta.currency`} bordered={false} style={{ width: "100%", borderBottom: "1px solid #d9d9d9", borderRadius: 0 }}>
                                                    {WRITABLE_CURRENCY_CODES.map((c) => (
                                                        <Option key={c} value={c}>
                                                            {c}
                                                        </Option>
                                                    ))}
                                                </EditableHeaderField>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={5}>
                                        <InvoiceAmountDisplay summary={amountSummary} />
                                    </Col>
                                </Row>

                                <InvoiceBalanceAlert summary={amountSummary} />

                                {/* 源文件信息 */}
                                {invoiceMeta.source_file && (
                                    <Row gutter={16} style={{ marginTop: 8 }}>
                                        {invoiceMeta.remarks && (
                                            <Col span={12}>
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    备注
                                                </Text>
                                                <EditableHeaderField
                                                    component={Input.TextArea}
                                                    value={invoiceMeta.remarks}
                                                    onChange={(e) => updateReconciliationData(`invoices[${invoiceIndex}].meta.remarks`, e.target.value)}
                                                    fieldPath={`invoices[${invoiceIndex}].meta.remarks`}
                                                    autoSize={{ minRows: 1, maxRows: 3 }}
                                                    bordered={false}
                                                    style={{ borderBottom: "1px solid #d9d9d9", borderRadius: 0, padding: "4px 0" }}
                                                />
                                            </Col>
                                        )}
                                        <Col span={12}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                源文件:
                                            </Text>
                                            {/* FILE_GROUP 多文件：逐一显示文件名链接 */}
                                            {invoiceMeta.source_file.record_type === "FILE_GROUP" && invoiceMeta.source_file.group_files?.length > 0 ? (
                                                <>
                                                    {invoiceMeta.source_file.group_files.map((gf, idx) => (
                                                        <Button
                                                            key={idx}
                                                            type="link"
                                                            size="small"
                                                            style={{ padding: "0 4px", fontSize: "12px" }}
                                                            onClick={async () => {
                                                                await openSingleFile(gf.object_key, gf.filename);
                                                            }}
                                                        >
                                                            {gf.filename}
                                                        </Button>
                                                    ))}
                                                </>
                                            ) : (
                                                invoiceMeta.source_file.filename && (
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        style={{ padding: "0 4px", fontSize: "12px" }}
                                                        onClick={async () => {
                                                            await handleViewOriginalFile(invoiceMeta.source_file);
                                                        }}
                                                    >
                                                        {invoiceMeta.source_file.filename}
                                                    </Button>
                                                )
                                            )}
                                            ({invoiceMeta.source_file.source_type})
                                        </Col>
                                    </Row>
                                )}
                            </>
                        ) : (
                            <Text type="secondary">暂无账单数据</Text>
                        )}

                        {sections && sections.length > 0 ? (
                            <Collapse
                                activeKey={expandedSections[invoiceIndex] || []}
                                onChange={(keys) => {
                                    setExpandedSections((prev) => ({
                                        ...prev,
                                        [invoiceIndex]: keys,
                                    }));
                                }}
                                size="small"
                                style={{ marginBottom: 16, marginTop: 16 }}
                            >
                                {sections.map((section, index) => {
                                    const actualLineItemCount = section.line_items?.length || 0;
                                    const expectedLineItemCount = section.expected_line_item_count;
                                    const subtotalInfo = calculateSectionSubtotal(section, invoiceMeta.currency, sections);
                                    const expectedSubTotal = section.expected_sub_total;
                                    const sectionId = section.section_id ?? section.section_identifier;
                                    const sectionBalance = sectionBalances.find((item) => String(item?.section_id) === String(sectionId));
                                    const sectionBalancePresentation = getSectionBalancePresentation(sectionBalance);

                                    // 获取币种和汇率信息
                                    const sectionCurrency = section.currency || invoiceMeta.currency;
                                    const isDifferentCurrency = section.currency && section.currency !== invoiceMeta.currency;
                                    const exchangeRate = section.additional_info?.exchange_rate;
                                    const exchangeRateInfo = exchangeRate
                                        ? Object.entries(exchangeRate)
                                              .map(([key, value]) => `${key}: ${value}`)
                                              .join(", ")
                                        : null;

                                    // 获取项目数量对比状态
                                    const itemCountComparison = getCountComparison(actualLineItemCount, expectedLineItemCount);
                                    // 获取小计对比状态 - 使用原币种金额对比
                                    const subtotalComparison = getAmountComparison(
                                        subtotalInfo.original,
                                        expectedSubTotal,
                                        sectionBalance?.section_balance_status,
                                    );

                                    return (
                                        <Panel
                                            header={
                                                <Space wrap onClick={(e) => e.stopPropagation()}>
                                                    <Input value={section.section_identifier} onChange={(e) => updateReconciliationData(`invoices[${invoiceIndex}].sections[${index}].section_identifier`, e.target.value)} bordered={false} style={{ width: 200, fontWeight: "bold", borderBottom: "1px dashed #d9d9d9" }} onClick={(e) => e.stopPropagation()} />
                                                    <Tooltip title={itemCountComparison.tooltip}>
                                                        <Tag color={itemCountComparison.status === COMPARE_STATUS.MATCH ? "blue" : "orange"}>
                                                            {actualLineItemCount} 项目
                                                            {expectedLineItemCount !== undefined && <span style={{ marginLeft: 4 }}>{itemCountComparison.icon}</span>}
                                                        </Tag>
                                                    </Tooltip>
                                                    {isDifferentCurrency && (
                                                        <Tooltip title={`独立币种: ${sectionCurrency}${exchangeRateInfo ? `\n汇率: ${exchangeRateInfo}` : ""}`}>
                                                            <Tag color="purple">币种: {sectionCurrency}</Tag>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title={subtotalComparison.tooltip}>
                                                        <Tag color={subtotalComparison.color}>
                                                            小计: {sectionCurrency} {subtotalInfo.original.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                            {isDifferentCurrency && (
                                                                <Text type="secondary" style={{ marginLeft: 4, fontSize: "11px" }}>
                                                                    (≈{invoiceMeta.currency} {subtotalInfo.converted.toLocaleString(undefined, { minimumFractionDigits: 2 })})
                                                                </Text>
                                                            )}
                                                            {sectionBalancePresentation.annotation && (
                                                                <Text
                                                                    style={{
                                                                        marginLeft: 4,
                                                                        fontSize: 11,
                                                                        color: sectionBalancePresentation.color,
                                                                    }}
                                                                >
                                                                    {sectionBalancePresentation.annotation}
                                                                </Text>
                                                            )}
                                                            {expectedSubTotal !== undefined && <span style={{ marginLeft: 4 }}>{subtotalComparison.icon}</span>}
                                                        </Tag>
                                                    </Tooltip>
                                                </Space>
                                            }
                                            key={`${invoiceIndex}-${index}`}
                                            extra={
                                                <Space onClick={(e) => e.stopPropagation()}>
                                                    {/* 表格修正 Fixer 链路尚未打通，暂时隐藏。门控已就绪，后续打通后取消注释即可。 */}
                                                    {/*
                                                    <Tooltip title="表格修正 Fixer：用于修正账单表格，不等同于阶段重跑">
                                                        <Button
                                                            type="dashed"
                                                            icon={<RobotOutlined style={{ color: "#722ed1" }} />}
                                                            size="small"
                                                            disabled={isReadOnly}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openAiFixModal(invoiceIndex, section.section_identifier);
                                                            }}
                                                            style={{ borderColor: "#b37feb", color: "#722ed1", backgroundColor: "transparent" }}
                                                        >
                                                            表格修正
                                                        </Button>
                                                    </Tooltip>
                                                    */}
                                                    <Popconfirm
                                                        title="确定删除此分节吗？"
                                                        onConfirm={(e) => {
                                                            e?.stopPropagation();
                                                            removeSection(invoiceIndex, index);
                                                        }}
                                                        onCancel={(e) => e?.stopPropagation()}
                                                        okText="确定"
                                                        cancelText="取消"
                                                    >
                                                        <Button type="text" danger icon={<DeleteOutlined />} size="small" onClick={(e) => e.stopPropagation()} />
                                                    </Popconfirm>
                                                </Space>
                                            }
                                        >
                                            {section.line_items && section.line_items.length > 0 ? (
                                                <Table
                                                    components={{
                                                        body: {
                                                            row: EditableRow,
                                                            cell: EditableCell,
                                                        },
                                                    }}
                                                    rowClassName={(record) => {
                                                        const isSelected = selectedLineItem?.invoiceIndex === invoiceIndex && selectedLineItem?.sectionIndex === index && selectedLineItem?.itemIndex === record.itemIndex;
                                                        return `editable-row ${isSelected ? "selected-row" : ""} ${isLineItemInactive(record) ? "voided-line-item-row" : ""}`;
                                                    }}
                                                    bordered
                                                    columns={generateLineItemColumns(invoiceIndex, index)}
                                                    dataSource={section.line_items.map((item, idx) => ({
                                                        ...item,
                                                        key: `${invoiceIndex}-${index}-${idx}`,
                                                        itemIndex: idx, // 用于追踪修改状态
                                                        sectionCurrency: sectionCurrency, // 添加小节币种信息
                                                    }))}
                                                    pagination={false}
                                                    size="small"
                                                    scroll={{ x: 1080 }}
                                                    loading={reconciliationState.status === "LOADING"}
                                                    onRow={() => ({
                                                        style: { cursor: "default" },
                                                    })}
                                                />
                                            ) : (
                                                <Text type="secondary">该分节无行项目数据</Text>
                                            )}
                                            <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
                                                <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => addLineItem(invoiceIndex, index)}>
                                                    添加行项目
                                                </Button>
                                            </div>
                                        </Panel>
                                    );
                                })}
                            </Collapse>
                        ) : (
                            <Text type="secondary">暂无账单明细数据</Text>
                        )}
                        <Button type="dashed" block icon={<PlusOutlined />} onClick={() => addSection(invoiceIndex)} style={{ marginTop: 8 }}>
                            添加分节
                        </Button>
                    </Card>
                </Badge.Ribbon>
            </div>
        );
    };

    // 根据筛选条件过滤账单
    const filteredInvoices = useMemo(() => {
        if (!invoices || invoices.length === 0) return [];

        if (viewFilter === "all") return invoices;

        return invoices.filter((invoice) => {
            const invoiceIdx = invoices.indexOf(invoice);
            const dirtyFields = reconciliationState?.dirtyFields || new Set();
            const hasUserModifications = Array.from(dirtyFields).some((path) => path.startsWith(`invoices[${invoiceIdx}]`));

            if (viewFilter === "problems_only") {
                const hasProblems = invoice.sections?.some((section, sectionIdx) =>
                    section.line_items?.some((item, itemIdx) => getLineState(item, invoiceIdx, sectionIdx, itemIdx).hasCurrentProblem),
                );
                return hasProblems;
            }

            if (viewFilter === "user_modified") {
                return hasUserModifications;
            }

            if (viewFilter === "flight_unmatched") {
                return invoice.sections?.some((section) =>
                    section.line_items?.some((item) => {
                        const hasFlightInfo = item.additional_info?.flight_number || item.additional_info?.aircraft_registration;
                        return hasFlightInfo && item.matched_flight_ids?.length === 0;
                    }),
                );
            }

            if (viewFilter === "flight_matched") {
                return invoice.sections?.some((section) => section.line_items?.some((item) => item.matched_flight_ids?.length > 0));
            }

            return true;
        });
    }, [invoices, viewFilter, reconciliationState, getLineState]);

    // 计算统计信息（基于过滤后的账单）
    const statistics = useMemo(() => {
        if (!filteredInvoices || filteredInvoices.length === 0) {
            return { totalItems: 0, problemItems: 0, matchedItems: 0, unmatchedItems: 0, modifiedItems: 0 };
        }

        let totalItems = 0;
        let problemItems = 0;
        let matchedItems = 0;
        let unmatchedItems = 0;
        let modifiedItems = 0;
        const dirtyFields = reconciliationState?.dirtyFields || new Set();

        filteredInvoices.forEach((invoice, invoiceIdx) => {
            invoice.sections.forEach((section, sectionIdx) => {
                section.line_items.forEach((item, itemIdx) => {
                    totalItems++;
                    const lineState = getLineState(item, invoiceIdx, sectionIdx, itemIdx);
                    const hasFlightInfo = item.additional_info?.flight_number || item.additional_info?.aircraft_registration;

                    // 统计航班匹配状态
                    if (item.matched_flight_ids?.length > 0) {
                        matchedItems++;
                    } else if (hasFlightInfo) {
                        unmatchedItems++;
                    }

                    if (lineState.hasCurrentProblem) {
                        problemItems++;
                    }
                    // 检查是否被用户修改
                    const fieldPath = lineItemFieldPath(invoiceIdx, sectionIdx, itemIdx);
                    if (dirtyFieldList(dirtyFields).some((path) => path === fieldPath || path.startsWith(fieldPath + "."))) {
                        modifiedItems++;
                    }
                });
            });
        });

        return { totalItems, problemItems, matchedItems, unmatchedItems, modifiedItems };
    }, [filteredInvoices, reconciliationState, getLineState]);

    // 纯嵌入式组件 — 不需要页面导航拦截（useBeforeUnload / customNavigate 已移除）

    if (reconciliationState.status === "LOADING") {
        return (
            <Card style={{ height: "100%" }}>
                <div style={{ textAlign: "center", padding: "50px" }}>
                    <Spin size="large" tip="加载对账数据中..." />
                </div>
            </Card>
        );
    }

    if (reconciliationState.status === "ERROR") {
        return (
            <Card style={{ height: "100%" }}>
                <Alert message="加载失败" description={reconciliationState.error} type="error" showIcon />
            </Card>
        );
    }

    return (
        <div style={{ display: "flex", flexDirection: "column", minHeight: "100%" }}>
            {/* Agent 分析结果 - 显示在顶部 */}
            {agentData && <AgentAnalysisViewer agentData={agentData} visible={showAgentAnalysis} onToggleVisibility={() => setShowAgentAnalysis(!showAgentAnalysis)} />}

            {/* 工具栏  暂时隐藏
            <div
                style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid #f0f0f0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: "#fff",
                }}
            >
                <Space>
                    <Text strong>视图筛选:</Text>
                    <Select value={viewFilter} onChange={setViewFilter} style={{ width: 150 }} suffixIcon={<FilterOutlined />}>
                        {Object.entries(RECONCILIATION_VIEW_FILTER_LABELS).map(([key, label]) => {
                            const filterValue = RECONCILIATION_VIEW_FILTER[key];

                            // 为不同筛选器添加相应的 badge
                            const getBadgeContent = () => {
                                switch (filterValue) {
                                    case RECONCILIATION_VIEW_FILTER.PROBLEMS_ONLY:
                                        return (
                                            <Badge count={statistics.problemItems} size="small">
                                                {label}
                                            </Badge>
                                        );
                                    case RECONCILIATION_VIEW_FILTER.FLIGHT_UNMATCHED:
                                        return (
                                            <Badge count={statistics.unmatchedItems} size="small">
                                                {label}
                                            </Badge>
                                        );
                                    case RECONCILIATION_VIEW_FILTER.FLIGHT_MATCHED:
                                        return (
                                            <Badge count={statistics.matchedItems} size="small" color="green">
                                                {label}
                                            </Badge>
                                        );
                                    case RECONCILIATION_VIEW_FILTER.USER_MODIFIED:
                                        return (
                                            <Badge count={statistics.modifiedItems} size="small" color="blue">
                                                {label}
                                            </Badge>
                                        );
                                    default:
                                        return label;
                                }
                            };

                            return (
                                <Option key={key} value={filterValue}>
                                    {getBadgeContent()}
                                </Option>
                            );
                        })}
                    </Select>

                    {agentData && (
                        <Button icon={<RobotOutlined />} onClick={() => setShowAgentAnalysis(!showAgentAnalysis)} type={showAgentAnalysis ? "primary" : "default"} size="small">
                            AI 分析
                        </Button>
                    )}
                </Space>

                <Space>
                    <PermissionGuard permission="settlement_ap_reconciliation:write">
                        <Button icon={<CheckOutlined />} disabled={statistics.problemItems === 0}>
                            批量绑定航班
                        </Button>
                    </PermissionGuard>
                </Space>
            </div>
*/}
            {/* 账单明细展示 */}
            <div style={{ flex: 1, padding: "16px" }}>
                {threeTierReadiness?.needs_manual_input && (
                    <Alert
                        type="warning"
                        showIcon
                        closable={false}
                        message="缺少结算金额"
                        description={threeTierReadiness.missing_reason || "请补充结算金额后再提交。"}
                        style={{ marginBottom: 12 }}
                    />
                )}

                <ContractMatchPanel contractMatchResult={contractMatchResult} updateReconciliationData={updateReconciliationData} invoices={invoices} threeTierReadiness={threeTierReadiness} />

                {filteredInvoices.length === 0 ? (
                    <div
                        style={{
                            textAlign: "center",
                            padding: "50px",
                            color: "#999",
                        }}
                    >
                        {viewFilter === "problems_only" ? "🎉 所有问题已处理完毕！" : viewFilter === "user_modified" ? "暂无修改项目" : "暂无数据"}
                    </div>
                ) : filteredInvoices.length === 1 ? (
                    // 单账单直接显示
                    renderInvoiceContent(filteredInvoices[0], 0)
                ) : (
                    // 多账单依次显示
                    filteredInvoices.map((invoice, index) => (
                        <div key={index} style={{ marginBottom: "24px" }}>
                            {renderInvoiceContent(invoice, index)}
                        </div>
                    ))
                )}
            </div>

            {/* 表格修正 Fixer 弹窗 */}
            <Modal
                title={
                    <Space>
                        <RobotOutlined style={{ color: "#722ed1" }} />
                        <span>表格修正 Fixer</span>
                        {aiFixModal.sectionIdentifier && <Tag color="purple">{aiFixModal.sectionIdentifier}</Tag>}
                    </Space>
                }
                open={aiFixModal.visible}
                onCancel={closeAiFixModal}
                onOk={handleAiFix}
                confirmLoading={aiFixModal.loading}
                okText="执行修正"
                cancelText="取消"
                width={600}
            >
                <div style={{ marginBottom: 16 }}>
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                        message="这是表格修正入口"
                        description="该入口会调用 AP Invoice Fixer 生成新的账单表格建议；它不是 OCR 识别、制表校验、分摊航班或验证航班阶段的重跑。若需要重跑阶段，请使用顶部操作菜单中的新版 Pipeline 节点。"
                    />
                    <Text type="secondary">请描述您发现的问题或需要修正的内容，AI 将根据您的描述结合原始文档进行表格修正。</Text>
                </div>
                <Input.TextArea
                    value={aiFixFeedback}
                    onChange={(e) => setAiFixFeedback(e.target.value)}
                    placeholder="例如：漏了一行燃油费 500 元&#10;或：第三行的航班号应该是 CA123 而不是 CA124"
                    rows={4}
                    maxLength={2000}
                    showCount
                    disabled={aiFixModal.loading}
                />
                {aiFixModal.loading && (
                    <div style={{ marginTop: 16, textAlign: "center" }}>
                        <Spin size="small" />
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                            表格修正 Fixer 正在分析并修正中...
                        </Text>
                    </div>
                )}
            </Modal>

        </div>
    );
};

export default ReconciliationWorkspace;
